import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, House, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';

type BreadcrumbItem = {
    name: string;
    to?: string;
    params?: Record<string, any>;
    search?: Record<string, any>;
    isActive?: boolean;
    subcategory?: {
        name: string;
        to: string;
        params?: Record<string, any>;
        search?: Record<string, any>;
    };
};

type BreadcrumbProps = {
    items: BreadcrumbItem[];
    showHome?: boolean;
};

export function Breadcrumb({ items, showHome = true }: BreadcrumbProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Mobile view: show last 2 items, rest in dropdown
    const visibleItems = items.length > 2 ? items.slice(-2) : items;
    const hiddenItems = items.length > 2 ? items.slice(0, -2) : [];

    return (
        <div>
            <div className="flex items-center flex-row mb-6 text-sm sm:text-base sm:hidden">
                {showHome && (
                    <>
                        <Link
                            to="/"
                            className="transition duration-500 inline"
                            inactiveProps={{
                                className:
                                'pb-2 font-semibold text-text-primary/60 hover:text-text-primary/70',
                            }}
                            activeProps={{
                                className: 'pb-2 font-semibold text-primary',
                            }}
                            activeOptions={{
                                exact: true,
                            }}
                        >
                            <House width={15} />
                        </Link>
                        <p className="pb-2 font-semibold text-text-primary/30 mx-3 select-none">
                            <ChevronRight width={15} />
                        </p>
                    </>
                )}

                {hiddenItems.length > 0 && (
                    <div className="relative flex items-center">
                        <a
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="pb-2 font-semibold text-text-primary/60 hover:text-text-primary/70 transition duration-500"
                        >
                            <div className="flex flex-row gap-2">
                                <MoreHorizontal width={15} />
                                <ChevronDown width={15} />
                            </div>
                        </a>

                        {isDropdownOpen && (
                            <>
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setIsDropdownOpen(false)}
                                />
                                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-[150px]">
                                    {hiddenItems.map(item => (
                                        <Link
                                            key={item.name}
                                            to={item.subcategory?.to || item?.to || ''}
                                            params={item.subcategory?.params || item?.params || undefined}
                                            search={item.subcategory?.search || item?.search || undefined}
                                            className="block px-4 py-2 text-sm font-semibold text-text-primary/60 hover:text-text-primary/70 hover:bg-gray-50 transition duration-500"
                                            onClick={() => setIsDropdownOpen(false)}
                                        >
                                            {item.name}
                                            {item.subcategory && `/${item.subcategory.name}`}
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}

                        <p className="pb-2 font-semibold text-text-primary/30 mx-3 select-none">
                            <ChevronRight width={15} />
                        </p>
                    </div>
                )}

                {visibleItems.map((item, index) => (
                    <div key={item.name} className="flex items-center">
                        <Link
                            to={item.subcategory?.to || item?.to || ''}
                            params={item.subcategory?.params || item?.params || undefined}
                            search={item.subcategory?.search || item?.search || undefined}
                            className="transition duration-500"
                            inactiveProps={{
                                className:
                                'pb-2 font-semibold text-text-primary/60 hover:text-text-primary/70',
                            }}
                            activeProps={{
                                className: 'pb-2 font-semibold text-text-black',
                            }}
                            activeOptions={{
                                exact: item.isActive !== false,
                                includeSearch: false,
                            }}
                        >
                            {item.name}
                            {item.subcategory && `/${item.subcategory.name}`}
                        </Link>

                        {index < visibleItems.length - 1 && (
                            <p className="pb-2 font-semibold text-text-primary/30 mx-1 select-none">
                                <ChevronRight width={15} />
                            </p>
                        )}
                    </div>
                ))}
            </div>
            <div className="sm:flex items-center mb-6 hidden">
                {showHome && (
                    <>
                        <Link
                            to="/"
                            className="transition duration-500"
                            inactiveProps={{
                                className:
                                'pb-2 font-semibold text-text-primary/60 hover:text-text-primary/70',
                            }}
                            activeProps={{
                                className: 'pb-2 font-semibold text-primary',
                            }}
                            activeOptions={{
                                exact: true,
                            }}
                        >
                            <House className="w-5 h-5" />
                        </Link>
                        <p className="pb-2 font-semibold text-text-primary/30 mx-3 select-none">
                            <ChevronRight className="h-5 w-5" />
                        </p>
                    </>
                )}

                {items.map((item, index) => (
                    <div key={item.name} className="flex items-center">
                        <Link
                            to={item.subcategory?.to || item?.to || ''}
                            params={item.subcategory?.params || item?.params || undefined}
                            search={item.subcategory?.search || item?.search || undefined}
                            className="transition duration-500 text-md"
                            inactiveProps={{
                                className:
                                'pb-2 font-semibold text-text-primary/60 hover:text-text-primary/70',
                            }}
                            activeProps={{
                                className: 'pb-2 font-semibold text-text-black',
                            }}
                            activeOptions={{
                                exact: item.isActive !== false,
                                includeSearch: false,
                            }}
                        >
                            {item.name}
                            {item.subcategory && `/${item.subcategory.name}`}
                        </Link>

                        {index < items.length - 1 && (
                            <p className="pb-2 font-semibold text-text-primary/30 mx-3 select-none">
                                <ChevronRight className="h-5 w-5" />
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
